<template>
    <v-layout
        row
        wrap
    >
        <!-- TITLE OF THE PAGE-->
        <h2 class="custom_nature">
            {{ $t('administration.nature.search.mainTitle') }}
        </h2>

        <!-- FORM WITH RESEARCH BUTTON -->
        <v-layout
            row
            wrap
            justify-center
            class="layout_nature"
        >
            <v-flex
                xs5
                class="sizer_flex"
            >
                <v-text-field
                    v-model="filter.label"
                    class="t_name"
                    dark
                    :label="$t('administration.nature.label.name')"
                    clearable
                    @keyup.enter="filterNaturesWithPaginationReset()"
                />
            </v-flex>
            <v-flex
                xs1
                justify-end
            >
                <v-btn
                    class="t_search"
                    color="white"
                    small
                    fab
                >
                    <v-icon
                        color="primary"
                        dark
                        @keyup.enter="filterNaturesWithPaginationReset()"
                        @click="filterNaturesWithPaginationReset()"
                    >
                        search
                    </v-icon>
                </v-btn>
            </v-flex>
        </v-layout>

        <!-- PROGRESS BAR -->
        <v-layout
            v-if="displaySpinner"
            align-center
            justify-center
            row
        >
            <v-progress-circular
                :size="70"
                :indeterminate="true"
                color="primary"
            />
        </v-layout>

        <!-- DATA TABLES -->
        <v-flex v-if="!displaySpinner">
            <v-data-table
                :headers="headers"
                :items="naturesList"
                hide-actions
                disable-initial-sort
                align-center
                justify-center
                class="elevation-1"
            >
                <template slot="no-data">
                    <td
                        colspan="6"
                        class="t_no_data text-xs-center"
                    >
                        {{ $t('administration.nature.search.none') }}
                    </td>
                </template>

                <template
                    slot="items"
                    slot-scope="props"
                >
                    <td class="align-items">
                        {{ props.item.label }}
                    </td>
                    <td class="align-items">
                        {{ props.item.code }}
                    </td>
                    <td class="align-items">
                        {{ props.item.description }}
                    </td>
                    <td class="align-items">
                        <v-chip
                            v-for="m in props.item.markets"
                            :key="id"
                            :class="'t_entry_market_'+m.code"
                        >
                            {{ m.code }}
                        </v-chip>
                    </td>
                    <td class="align-items">
                        <router-link
                            class="t_edit"
                            tag="button"
                            :to="{name: 'NatureEdit', params: { id: props.item.id }}"
                        >
                            <v-icon class="edit-icon">
                                edit
                            </v-icon>
                        </router-link>
                        <v-icon
                            slot="activator"
                            class="t_delete edit-icon"
                            @click="openConfirmationDialog(props.item.id)"
                        >
                            delete
                        </v-icon>
                    </td>
                </template>
            </v-data-table>

            <v-dialog
                v-model="dialog"
                class="t_confirm_delete_dialog"
                persistent
                max-width="350"
                icon
            >
                <v-card>
                    <v-card-title class="headline">
                        {{ $t('administration.nature.manage.delete.title') }}
                    </v-card-title>
                    <v-divider />
                    <v-card-text>{{ $t('administration.nature.manage.delete.message') }}</v-card-text>
                    <v-divider />
                    <v-card-actions>
                        <v-spacer />
                        <v-btn
                            color="primary"
                            small
                            flat
                            @click.native="dialog = false"
                        >
                            {{ $t('administration.nature.manage.delete.cancel') }}
                        </v-btn>
                        <v-btn
                            class="t_confirm_delete"
                            color="primary"
                            small
                            flat
                            @click.native="deleteNature()"
                        >
                            {{ $t('administration.nature.manage.delete.confirm') }}
                        </v-btn>
                    </v-card-actions>
                </v-card>
            </v-dialog>
        </v-flex>

        <!-- PAGINATION -->
        <v-flex
            v-if="!!naturesList && naturesList.length > 0"
            xs12
            class="nature-list"
        >
            <v-pagination
                :length="pageable.totalPages"
                :total-visible="5"
                :value="pageable.page"
                circle
                @input="changePage"
            />
        </v-flex>

        <!-- ADD NEW NATURE -->
        <v-tooltip top>
            <v-btn
                slot="activator"
                color="primary"
                small
                fab
                dark
                icon
                class="t_add icon-add-custom"
                :to="{name: 'NatureAdd'}"
            >
                <v-icon dark>
                    add
                </v-icon>
            </v-btn>
            <span>{{ $t('administration.nature.search.action.add') }}</span>
        </v-tooltip>
    </v-layout>
</template>

<script>

import axios from 'axios';
import EventBus from '../../../utils/event-bus.js';
import ConstantEvent from '../../../shared/constant/constantEvent.js';
import {mapState} from 'vuex';

export default {
    name:'NatureSearch',
    data () {
        return {
            naturesList: [],
            displaySpinner: true,
            pageable: {page: 1, size: 10,  direction: 'ASC', property: "label"},
            latestFilter: {},
            filter: {},
            dialog: false,
            headers:[
                {
                    text: this.$t('administration.nature.label.name'),
                    value:"label",
                    sortable: false,
                    align: 'center',
                },
                {
                    text: this.$t('administration.nature.label.abbreviation'),
                    value:"code",
                    sortable: false,
                    align: 'center',
                },
                {
                    text: this.$t('administration.nature.label.description'),
                    value:"description",
                    sortable: false,
                    align: 'center',
                },
                {
                    text: this.$t('administration.nature.label.markets'),
                    value: "markets",
                    sortable: false,
                    align: 'center'
                },
                {
                    text: this.$t('administration.nature.label.actions'),
                    value:"actions",
                    sortable: false,
                    align: 'center',
                }
            ],
        }
    },
    created(){
        if (this.login) this.filterNatures();
    },
    computed: {
        ...mapState({
            login: state => state.userStore.userPreferences.login,
        }),
    },
    watch: {
        login (newValue, oldValue) {
            this.filterNatures();
        }
    },
    methods: {
        /**
             * Search natures by filter, reset page index to 1
             */
        filterNaturesWithPaginationReset() {
            this.pageable.page = 1;
            this.filterNatures();
        },
        /**
             * Search natures by filter
             */
        filterNatures() {
            this.latestFilter.label = this.filter.label;
            this.displaySpinner = true;
            axios.post(
                'administration/nature/filter',
                this.filter,
                {params: this.pageable}
            ).then( (result) => {
                this.displaySpinner = false;
                this.naturesList = result.data.content;
                this.pageable.totalElements = result.data.totalElements;
                this.pageable.totalPages = result.data.totalPages;
                this.pageable.page = result.data.page;
            });
        },
            
        /**
             * change page and navigate to the selected page
             * @param page
             */
        changePage(page) {
            this.pageable.page = page;
            this.filterNatures();
        },
        openConfirmationDialog(natureId) {
            this.dialog = true;
            this.natureIdToDelete = natureId;
        },
        deleteNature() {
            this.dialog = false;
            axios.delete('administration/nature/' + this.natureIdToDelete).then( () => {
                EventBus.$emit(ConstantEvent.ADD_NOTIFICATION, {
                    message: 'administration.nature.manage.delete.success',
                    type: 'SUCCEED'
                });
                if ( this.naturesList.length <= 1 && this.pageable.page > 1) {
                    this.pageable.page = this.pageable.page - 1;
                }
                this.filterNatures();
            });
        }
    }
}
</script>

<style scoped>
    .custom_nature{
        background-color: #355B7F;color:white;padding-top:25px;text-align: center;width:100vw;
    }
    .layout_nature{
        background-color: #355B7F;color:white;padding-top:10px;
    }
    .sizer_flex{
        width:100vw;
    }
    .align-items{
        text-align: center;
    }
    .icon-customer{
        margin-left: 15px;
    }
    .nature-list{
        transform: translate(45%,0);margin-top:15px;
    }
    .icon-add-custom{
        position: fixed; right: 50px; bottom: 0;
    }
</style>
